import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, CheckCircle2, Circle, XCircle, Loader2, PackageCheck, Truck } from "lucide-react";
import { api, formatINR } from "../lib/api";
import { toast } from "sonner";

const STEPS = [
  { key: "placed", label: "Order Placed" },
  { key: "paid", label: "Payment Confirmed" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSearched(true);
    setNotFound(false);
    try {
      const r = await api.get(`/orders/track/${orderNumber.trim()}`, { params: { email: email.trim() } });
      setOrder(r.data);
    } catch (err) {
      setOrder(null);
      if (err?.response?.status === 404) {
        setNotFound(true);
        toast.error(err?.response?.data?.detail || "No order found with that order number and email.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const expired = order?.status === "expired";
  const failed = order?.payment_status === "FAILED";
  const currentStepIndex = !order ? -1 : (
    order.status === "delivered" ? 3 :
    order.status === "shipped" ? 2 :
    order.payment_status === "PAID" ? 1 : 0
  );

  return (
    <div data-testid="track-order-page" className="min-h-screen max-w-2xl mx-auto px-6 py-16">
      <div className="overline text-[#D96A1B] mb-2">Order Status</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter">Track My Order</h1>
      <p className="text-white/60 font-body mt-3">Enter your order number and the email you used at checkout.</p>

      <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-3">
        <input
          data-testid="to-order-number"
          required
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          placeholder="Order Number (e.g. MF2026...)"
          className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm sm:col-span-1"
        />
        <input
          data-testid="to-email"
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email used at checkout"
          className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm sm:col-span-1"
        />
        <button data-testid="to-submit" disabled={loading} className="btn-orange justify-center py-3 sm:col-span-2">
          {loading ? (<><Loader2 size={16} className="animate-spin" /> Searching…</>) : (<><Search size={16} /> Track Order</>)}
        </button>
      </form>

      {searched && !loading && !order && (
        <div data-testid="to-not-found" className="mt-10 text-center text-white/50 font-body border border-white/10 py-12">
          No matching order found. Double-check your order number and email.
        </div>
      )}

      {order && (
        <div data-testid="to-result" className="mt-10 border border-white/10 p-6">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-display text-xl">#{order.order_number}</div>
            <div className="font-display text-[#D96A1B]">{formatINR(order.total)}</div>
          </div>
          <div className="text-xs text-white/50 font-body mt-1">Placed {new Date(order.created_at).toLocaleString("en-IN")}</div>

          {(expired || failed) ? (
            <div data-testid="to-status-negative" className="mt-6 flex items-center gap-3 text-red-400">
              <XCircle size={22} />
              <span className="font-display uppercase tracking-widest text-sm">
                {expired ? "Order Expired — Payment Window Closed" : "Payment Failed"}
              </span>
            </div>
          ) : (
            <div data-testid="to-timeline" className="mt-8 space-y-6">
              {STEPS.map((step, i) => {
                const done = i <= currentStepIndex;
                const at = { placed: order.created_at, paid: order.paid_at, shipped: order.shipped_at, delivered: order.delivered_at }[step.key];
                const Icon = step.key === "shipped" ? Truck : step.key === "delivered" ? PackageCheck : CheckCircle2;
                return (
                  <div key={step.key} data-testid={`to-step-${step.key}`} className="flex items-start gap-4">
                    <div className={`shrink-0 ${done ? "text-[#D96A1B]" : "text-white/25"}`}>
                      {done ? <Icon size={20} /> : <Circle size={20} />}
                    </div>
                    <div>
                      <div className={`font-display uppercase tracking-widest text-sm ${done ? "text-white" : "text-white/40"}`}>{step.label}</div>
                      {at && <div className="text-xs text-white/45 font-body mt-0.5">{new Date(at).toLocaleString("en-IN")}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 border-t border-white/10 pt-5 space-y-2">
            {order.items.map((i) => (
              <div key={i.product_id} className="flex items-center gap-3 text-sm text-white/70 font-body">
                <img src={i.image} alt="" className="w-8 h-10 object-cover" />
                <span className="flex-1 truncate">{i.name} × {i.quantity}</span>
              </div>
            ))}
            <div className="text-xs text-white/50 font-body pt-2">Shipping to {order.city}, {order.state}</div>
          </div>
        </div>
      )}
    </div>
  );
}
